<template>
    <main class="flex-1 relative pb-8 z-0 overflow-y-auto">
        <PageHeader>
            <h1 class="ml-3 text-2xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full">
                <span class="flex flex-row items-center">
                    <Loader v-if="isFetchPending" class="-sm m-2"/>
                    <template v-else>Import de sources</template>
                </span>
            </h1>
        </PageHeader>

        <div class="mt-8 lg:max-w-6xl lg:mx-auto">
            <div class="m-4 space-y-6">

                <div v-if="current" class="bg-white shadow rounded-lg p-6">
                    <div class="flex items-center justify-between mb-2">
                        <h2 class="text-lg font-bold text-gray-900">
                            {{ current.filename }}
                            <span class="ml-2 text-sm font-normal text-gray-500">Dossier sources/{{ current.slug }}</span>
                        </h2>
                        <StatusBadge :status="current.status"/>
                    </div>

                    <p class="text-sm text-gray-600 mb-3">
                        {{ current.step || 'En attente du worker : le démarrage peut prendre jusqu\'à une minute.' }}
                        <span v-if="elapsed" class="text-gray-400">— {{ elapsed }}</span>
                    </p>

                    <div
                        class="w-full bg-gray-200 rounded-full h-2.5 mb-4"
                        role="progressbar"
                        :aria-valuenow="current.progress"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        :aria-label="'Progression de l\'import : ' + (current.step || 'en attente')"
                    >
                        <div
                            class="h-2.5 rounded-full transition-all duration-500"
                            :class="current.status === 'failed' ? 'bg-red-500' : 'bg-custom-600'"
                            :style="{ width: current.progress + '%' }"
                        ></div>
                    </div>

                    <p v-if="current.error" class="text-sm text-red-600 mb-3">{{ current.error }}</p>

                    <DeliveryIssues :issues="current.issues" :filename="current.filename"/>

                    <pre
                        v-if="current.output"
                        ref="log"
                        class="bg-gray-900 text-gray-100 text-xs rounded-md p-4 h-64 overflow-auto whitespace-pre-wrap"
                    >{{ current.output }}</pre>

                    <div v-if="isFinished(current)" class="mt-4">
                        <div class="flex flex-wrap gap-3 items-center">
                            <a
                                v-if="current.preview_url"
                                :href="current.preview_url"
                                target="_blank"
                                rel="noopener"
                                class="px-4 py-2 text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800"
                            >
                                Prévisualiser l'orbitale ↗
                            </a>
                            <router-link
                                v-if="current.project_id"
                                :to="{ name: 'project-show', params: { id: current.project_id } }"
                                class="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Réglages du projet
                            </router-link>
                            <button
                                v-if="current.can_relaunch && ! isSucceededDryRun"
                                type="button"
                                :disabled="isRelaunchPending"
                                @click="handleRelaunch()"
                                class="px-4 py-2 text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {{ isRelaunchPending ? 'Relance…' : 'Relancer cet import' }}
                            </button>
                            <button
                                v-if="isSucceededDryRun"
                                type="button"
                                :disabled="isRelaunchPending"
                                @click="handleRelaunch(false)"
                                class="px-4 py-2 text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {{ isRelaunchPending ? 'Relance…' : 'Lancer l\'import réel' }}
                            </button>
                            <button
                                v-if="isSucceededDryRun"
                                type="button"
                                :disabled="isRelaunchPending"
                                @click="handleRelaunch()"
                                class="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {{ isRelaunchPending ? 'Relance…' : 'Refaire la simulation' }}
                            </button>
                            <button
                                v-if="current.can_restore_plans"
                                type="button"
                                :disabled="isRestorePending"
                                @click="handleRestorePlans()"
                                class="px-4 py-2 text-sm font-medium rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {{ isRestorePending ? 'Restauration…' : 'Restaurer les plans précédents' }}
                            </button>
                            <button
                                type="button"
                                @click="current = null"
                                class="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                                Nouvel import
                            </button>
                            <button
                                v-if="current.status === 'failed' || isSucceededDryRun"
                                type="button"
                                @click="handleDelete(current)"
                                class="px-4 py-2 text-sm font-medium rounded-md border border-red-300 text-red-700 hover:bg-red-50"
                            >
                                Supprimer cet import
                            </button>
                        </div>

                        <p v-if="current.can_restore_plans" class="mt-2 text-xs text-gray-500">
                            Les plans que cet import a remplacés sont encore sur le serveur. La
                            restauration les remet en service et met de côté ceux d'aujourd'hui :
                            elle est donc réversible à son tour. Ils sont effacés
                            {{ current.previous_plans_retention_days }} jours après leur mise de
                            côté, ou au prochain import de plans sur ce bâtiment.
                        </p>

                        <p v-if="isSucceededDryRun" class="mt-2 text-xs text-gray-500">
                            La livraison est conservée sur le serveur : « Lancer l'import réel »
                            la rejoue avec les mêmes réglages, cette fois en enregistrant les
                            données, sans avoir à redéposer le fichier.
                        </p>
                        <p v-else-if="current.can_relaunch" class="mt-2 text-xs text-gray-500">
                            La livraison est conservée sur le serveur : la relance la rejoue avec
                            les mêmes réglages, sans avoir à redéposer le fichier. Corrigez
                            d'abord la cause de l'échec.
                        </p>
                        <p v-else-if="current.status === 'failed'" class="mt-2 text-xs text-gray-500">
                            La livraison n'est plus sur le serveur : pour réessayer, redéposez le
                            fichier avec « Nouvel import ».
                        </p>

                        <p v-if="current.preview_url" class="mt-2 text-xs text-gray-500 break-all">
                            {{ current.preview_url }}
                        </p>
                        <p v-else-if="current.status === 'succeeded'" class="mt-2 text-xs text-gray-500">
                            Simulation : aucun projet n'a été créé, il n'y a donc rien à prévisualiser.
                            Utilisez « Lancer l'import réel » pour obtenir le lien.
                        </p>
                    </div>
                </div>

                <form v-else class="bg-white shadow rounded-lg p-6 space-y-5" @submit.prevent="handleSubmit">
                    <FilePckr
                        id="archive"
                        name="archive"
                        label="Livraison du prestataire (.zip)"
                        accepted-file-types="application/zip,application/x-zip-compressed"
                        :chunk-size="8000000"
                        :errors="archiveRefusal ? [archiveRefusal] : errors.archive"
                        :before-add-file="checkArchive"
                        chunk-uploads
                        with-validation
                        required
                        @startLoad="isUploading = true"
                        @endLoad="handleArchiveUploaded"
                    />

                    <div class="-mt-2 space-y-2">
                        <details class="text-xs text-gray-600">
                            <summary class="cursor-pointer text-sm font-medium text-custom-600 hover:text-custom-800">
                                Format attendu de la livraison
                            </summary>
                            <div class="mt-2 space-y-2">
                                <p>
                                    <strong>Livraison complète</strong>&nbsp;: un dossier «&nbsp;Images&nbsp;»
                                    (les vues de la maquette) et un dossier «&nbsp;Zones&nbsp;» (un
                                    sous-dossier par étage, un fichier par logement, par exemple
                                    a001_t3.txt pour le logement A001 en T3). En option&nbsp;: «&nbsp;PDV&nbsp;»
                                    (les plans, dans des sous-dossiers «&nbsp;JPG&nbsp;» et «&nbsp;PDF&nbsp;»
                                    ou mélangés directement dans «&nbsp;PDV&nbsp;»),
                                    «&nbsp;Logo&nbsp;» et le brief .xlsx.
                                </p>
                                <p>
                                    Plusieurs bâtiments&nbsp;: un sous-dossier par bâtiment dans chacun de ces
                                    dossiers, avec des noms qui se correspondent («&nbsp;Maquette&nbsp;F&nbsp;»,
                                    «&nbsp;Maquette&nbsp;F&nbsp;-&nbsp;zones&nbsp;», «&nbsp;F&nbsp;»).
                                </p>
                                <p>
                                    <strong>Plans seuls</strong>&nbsp;: un dossier «&nbsp;PDV&nbsp;», ou directement
                                    des dossiers «&nbsp;jpg&nbsp;» et/ou «&nbsp;pdf&nbsp;», avec un plan par
                                    logement nommé comme lui (A001.jpg).
                                </p>
                                <p>
                                    Ces dossiers peuvent être directement dans le zip ou regroupés dans un
                                    seul dossier. Les autres fichiers sont ignorés.
                                </p>
                            </div>
                        </details>

                        <p class="text-xs text-gray-500">
                            Une livraison ne contenant que le dossier des plans est acceptée :
                            elle met à jour les plans d'un projet existant sans toucher aux images ni
                            aux zones. Elle <strong>remplace l'intégralité</strong> des plans du
                            bâtiment visé — le prestataire doit donc livrer le jeu complet à chaque fois.
                        </p>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select
                            id="target"
                            name="target"
                            label="Projet"
                            tooltip="«&nbsp;Alimenter un projet existant&nbsp;» met à jour un projet déjà importé, avec une livraison complète ou des plans seuls."
                            :options="targetOptions"
                            v-model="target"
                            required
                        />

                        <Input
                            v-if="target === 'new'"
                            id="project_name"
                            name="project_name"
                            label="Nom du projet à créer"
                            :tooltip="siteAddress"
                            :errors="errors.project_name"
                            v-model="form.project_name"
                            required
                        />

                        <Select
                            v-else
                            id="project_id"
                            name="project_id"
                            label="Projet à alimenter"
                            :options="projectOptions"
                            :errors="errors.project_id"
                            v-model="form.project_id"
                            searchable
                            required
                        />
                    </div>

                    <div v-if="buildingOptions.length > 1" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select
                            id="building"
                            name="building"
                            label="Bâtiment cible"
                            tooltip="Ne sert qu'aux livraisons de plans seuls : elles visent un bâtiment et un seul. Une livraison complète l'ignore."
                            :options="buildingOptions"
                            :errors="errors.building"
                            v-model="form.building"
                        />
                    </div>

                    <div v-if="target === 'new'" class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select
                            id="topology"
                            name="topology"
                            label="Typologie"
                            tooltip="Présentation des logements sur le site&nbsp;: en T (T1, T2…) ou en nombre de chambres. Le brief, s'il est appliqué, peut la modifier."
                            :options="topologyOptions"
                            v-model="form.topology"
                        />

                        <Select
                            v-if="isRoot"
                            id="corporation_id"
                            name="corporation_id"
                            label="Corporation"
                            tooltip="Le nouveau site reprend les réglages du dernier site de cette corporation, images exclues."
                            :options="corporationOptions"
                            v-model="form.corporation_id"
                            searchable
                        />
                    </div>

                    <div class="space-y-3 border-t border-gray-200 pt-4">
                        <Switch
                            id="create_categories"
                            name="create_categories"
                            label="Créer les typologies absentes du projet (T1, T5…)"
                            tooltip="Les typologies sont lues dans les noms des fichiers de zones (a001_t3&nbsp;: T3). Désactivé, celles qui manquent au projet sont seulement signalées, et leurs logements restent sans typologie."
                            v-model="form.create_categories"
                            is-inline
                        />
                        <Switch
                            id="with_brief"
                            name="with_brief"
                            label="Appliquer le brief xlsx aux réglages"
                            tooltip="Le fichier .xlsx placé à la racine de la livraison remplace par ses valeurs les réglages du site, du projet et des bâtiments. Ignoré pour une livraison de plans seuls."
                            v-model="form.with_brief"
                            is-inline
                        />
                        <Switch
                            id="prune"
                            name="prune"
                            label="Synchroniser : supprimer les étages et unités absents de la livraison"
                            tooltip="Supprime les bâtiments, étages et logements absents de la livraison, ainsi que les contacts rattachés à ces logements. Un bâtiment livré sans dossier «&nbsp;Zones&nbsp;» n'est pas touché. Refusé pour une livraison de plans seuls."
                            v-model="form.prune"
                            is-inline
                        />
                        <Switch
                            id="dry_run"
                            name="dry_run"
                            label="Simulation : vérifie la livraison sans rien modifier"
                            tooltip="La livraison est préparée dans un espace temporaire, effacé à la fin&nbsp;: rien n'est enregistré, ni en base ni sur le site. Le rapport d'écarts permet de la vérifier avant l'import réel."
                            v-model="form.dry_run"
                            is-inline
                        />
                    </div>

                    <div class="flex items-center justify-end gap-3">
                        <span v-if="isUploading" class="text-sm text-gray-500">Envoi de l'archive en cours…</span>
                        <button
                            type="submit"
                            :disabled="!canSubmit"
                            class="px-4 py-2 text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Lancer l'import
                        </button>
                    </div>

                    <p class="text-xs text-gray-500">
                        L'import démarre à la minute suivante et dure plusieurs minutes.
                        Vous pouvez quitter cette page, il continue côté serveur.
                    </p>
                </form>

                <div class="bg-white border border-red-200 rounded-lg p-6">
                    <h2 class="text-lg font-bold text-red-700">Supprimer un projet</h2>
                    <p class="text-sm text-gray-600 mt-1">
                        Efface le projet, son domaine, ses bâtiments, étages, unités, médias et ses
                        fichiers sur le disque. Utile pour repartir de zéro après un import raté.
                        Un inventaire est affiché et le nom du projet doit être saisi pour confirmer.
                    </p>

                    <div class="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
                        <div class="flex-1">
                            <Select
                                id="purge_project_id"
                                name="purge_project_id"
                                label="Projet à supprimer"
                                :options="projectOptions"
                                v-model="purgeProjectId"
                                searchable
                            />
                        </div>
                        <button
                            type="button"
                            :disabled="! purgeProjectId"
                            class="px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-900 disabled:opacity-40 disabled:cursor-not-allowed"
                            @click="handlePurge"
                        >
                            Supprimer…
                        </button>
                    </div>
                </div>

                <div class="bg-white shadow rounded-lg overflow-hidden">
                    <h2 class="text-lg font-bold text-gray-900 px-6 py-4 border-b border-gray-200">Historique</h2>

                    <p v-if="imports.length === 0" class="px-6 py-4 text-sm text-gray-500">Aucun import pour le moment.</p>

                    <ul v-else class="divide-y divide-gray-200">
                        <li v-for="item in recentImports" :key="item.id">
                            <button
                                type="button"
                                class="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-custom-600"
                                @click="handleOpen(item)"
                            >
                            <div class="min-w-0">
                                <p class="text-sm font-medium text-gray-900 truncate">{{ item.filename }}</p>
                                <p class="text-xs text-gray-500">
                                    Dossier {{ item.slug }} · {{ formatDate(item.created_at) }}
                                    <span v-if="item.options && item.options.dry_run"> · simulation</span>
                                </p>
                            </div>
                            <div class="flex items-center gap-2 shrink-0">
                                <span
                                    v-if="item.issues_count > 0"
                                    class="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800"
                                >
                                    {{ item.issues_count }} écart(s)
                                </span>
                                <StatusBadge :status="item.status"/>
                            </div>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import PageHeader from "../../components/partials/page-header";
import Loader from "../../components/partials/loader";
import FilePckr from "../../components/form/file-pckr";
import Input from "../../components/form/input";
import Select from "../../components/form/select";
import Switch from "../../components/form/switch";
import StatusBadge from "./status-badge";
import DeliveryIssues from "./delivery-issues";
import { archiveEntries } from "../../composables/archiveEntries";
import { archiveKind, deliveryFolders, unknownDeliveryMessage } from "../../../../../scripts/support/delivery-kind";

const POLL_INTERVAL = 2000;
const HISTORY_SIZE = 10;

// Letters NFD does not decompose, transliterated as Str::ascii() does.
const TRANSLITERATIONS = { 'œ': 'oe', 'Œ': 'OE', 'æ': 'ae', 'Æ': 'AE', 'ß': 'ss', 'ø': 'o', 'Ø': 'O', 'ł': 'l', 'Ł': 'L', 'đ': 'd', 'Đ': 'D' };

// Mirrors Str::slug() as CreateBuildingsFloorsUnitsCommand::hostFor() uses it: "@" reads "at",
// anything but letters, digits, spaces and dashes is dropped (an apostrophe joins its words),
// then spaces and dashes collapse into one dash.
function siteSlug(name) {
    return String(name)
        .replace(/[œŒæÆßøØłŁđĐ]/g, letter => TRANSLITERATIONS[letter])
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/_+/g, '-')
        .replace(/@/g, '-at-')
        .toLowerCase()
        .replace(/[^-a-z0-9\s]+/g, '')
        .replace(/[-\s]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export default {
    beforeMount() {
        this.$store.dispatch('source_imports/getList', {per_page: HISTORY_SIZE});
        this.$store.dispatch('projects/getList', {per_page: 200, includes: ['buildings']});

        if (this.isRoot) {
            this.$store.dispatch('corporations/getList', {per_page: 200});
        }
    },
    mounted() {
        this.poller = setInterval(this.pollRunningImport, POLL_INTERVAL);
    },
    beforeUnmount() {
        clearInterval(this.poller);
    },
    data() {
        return {
            poller: null,
            now: Date.now(),
            current: null,
            target: 'new',
            isUploading: false,
            purgeProjectId: null,
            isRelaunchPending: false,
            isRestorePending: false,
            archiveRefusal: null,
            form: {
                archive: null,
                project_name: '',
                project_id: null,
                corporation_id: null,
                building: '',
                topology: 't',
                create_categories: true,
                with_brief: true,
                dry_run: false,
                prune: false,
            },
        }
    },
    computed: {
        isFetchPending() {
            return this.$store.state.source_imports.isFetchListPending;
        },
        isSavePending() {
            return this.$store.state.source_imports.isSavePending;
        },
        errors() {
            return this.$store.state.source_imports.errors || {};
        },
        imports() {
            return this.$store.state.source_imports.list;
        },
        // The server sends the newest first, but STORE_ITEM appends imports created on this
        // page at the end of the list, which then outgrows the page it was fetched with.
        recentImports() {
            return [...this.imports].sort((first, second) => second.id - first.id).slice(0, HISTORY_SIZE);
        },
        /**
         * La fiche ouverte disparaît avec sa ligne : la modale de suppression agit sur le store,
         * elle n'a pas la main sur cette vue. Une computed et non un watcher sur `imports` :
         * la suppression retire l'élément par splice, sans remplacer le tableau, ce qu'un
         * watcher sur la référence ne verrait pas.
         */
        currentIsListed() {
            return ! this.current || this.imports.some(item => item.id === this.current.id);
        },
        /**
         * Une simulation réussie garde son archive : elle reste rejouable pour de bon.
         */
        isSucceededDryRun() {
            return this.current !== null
                && this.current.status === 'succeeded'
                && Boolean(this.current.options && this.current.options.dry_run)
                && this.current.can_relaunch;
        },
        isRoot() {
            return this.$store.getters['auth/isRoot'];
        },
        elapsed() {
            if (! this.current || ! this.current.started_at) {
                return null;
            }

            const end = this.current.finished_at ? new Date(this.current.finished_at) : this.now;
            const seconds = Math.max(0, Math.round((end - new Date(this.current.started_at)) / 1000));

            return seconds < 60 ? seconds + ' s' : Math.floor(seconds / 60) + ' min ' + (seconds % 60) + ' s';
        },
        canSubmit() {
            if (this.isUploading || this.isSavePending || !this.form.archive) {
                return false;
            }

            return this.target === 'new' ? this.form.project_name.length > 0 : this.form.project_id !== null;
        },
        // The address the new project gets: its DNS record has to exist before the import.
        siteAddress() {
            const slug = siteSlug(this.form.project_name);

            return slug ? 'Adresse du site : ' + slug + '.infos-projet.app' : '';
        },
        targetOptions() {
            return [
                {value: 'new', label: 'Créer un nouveau projet'},
                {value: 'existing', label: 'Alimenter un projet existant'},
            ];
        },
        topologyOptions() {
            return [
                {value: 't', label: 'T (T1, T2, T3…)'},
                {value: 'chamber', label: 'Chambres'},
            ];
        },
        projectOptions() {
            return this.$store.state.projects.list.map(project => ({value: project.id, label: project.name}));
        },
        selectedProject() {
            if (this.target !== 'existing' || this.form.project_id === null) {
                return null;
            }

            return this.$store.state.projects.list.find(project => project.id === this.form.project_id) || null;
        },
        /**
         * Une livraison de plans vise un bâtiment et un seul. Le champ n'a donc de sens que
         * sur un projet qui en compte plusieurs : ailleurs, le script le déduit tout seul.
         */
        buildingOptions() {
            const buildings = this.selectedProject?.buildings?.data || [];

            if (buildings.length < 2) {
                return [];
            }

            return [
                {value: '', label: 'Déduire automatiquement'},
                ...buildings.map(building => ({value: building.name, label: building.name})),
            ];
        },
        corporationOptions() {
            return this.$store.state.corporations.list.map(corporation => ({value: corporation.id, label: corporation.name}));
        },
    },
    watch: {
        // Le bâtiment retenu appartient au projet précédent : le garder ferait refuser l'import
        // par prepare_sources, sur un champ que le formulaire n'affiche même plus.
        'form.project_id'() {
            this.form.building = '';
        },
        currentIsListed(listed) {
            if (! listed) {
                this.current = null;
            }
        },
        // Le journal grossit pendant l'exécution : on garde la vue collée au bas.
        'current.output'() {
            this.$nextTick(() => {
                if (this.$refs.log) {
                    this.$refs.log.scrollTop = this.$refs.log.scrollHeight;
                }
            });
        },
    },
    methods: {
        handlePurge() {
            this.$store.dispatch('modals/open', { name: 'project-purge', data: this.purgeProjectId });
            this.purgeProjectId = null;
        },
        // La liste ne transporte ni le journal ni le détail des écarts : la fiche les charge.
        handleOpen(item) {
            this.current = item;

            this.$store.dispatch('source_imports/refresh', item.id).then(detailed => {
                if (detailed) {
                    this.current = detailed;
                }
            });
        },
        handleRelaunch(dryRun) {
            this.isRelaunchPending = true;

            this.$store.dispatch('source_imports/relaunch', {id: this.current.id, dry_run: dryRun})
                .then(relaunched => {
                    if (relaunched) {
                        this.current = relaunched;
                    }
                })
                .finally(() => { this.isRelaunchPending = false; });
        },
        handleDelete(item) {
            this.$store.dispatch('modals/open', { name: 'source_import-delete', data: item.id });
        },
        isFinished(item) {
            return item.status === 'succeeded' || item.status === 'failed';
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString('fr-BE', {dateStyle: 'short', timeStyle: 'short'}) : '';
        },
        /**
         * A zip keeps its central directory in its last bytes: reading it costs a few hundred KB,
         * where a badly laid out delivery used to be refused only after its whole upload. A read
         * that fails lets the upload go, and the server decides as before.
         */
        async checkArchive(item) {
            let entries;

            try {
                entries = await archiveEntries(item.file);
            } catch (error) {
                this.archiveRefusal = null;

                return true;
            }

            this.archiveRefusal = archiveKind(entries) === 'unknown'
                ? unknownDeliveryMessage(deliveryFolders(entries))
                : null;

            return this.archiveRefusal === null;
        },
        handleArchiveUploaded(files) {
            this.isUploading = false;

            if (files.length === 0) {
                this.form.archive = null;

                return;
            }

            this.form.archive = files[0].serverId;

            if (this.form.project_name === '') {
                this.form.project_name = this.suggestProjectName(files[0].filename);
            }
        },
        /**
         * Confort de saisie seulement : le dossier de sources est nommé d'après le projet
         * choisi, pas d'après l'archive. Retire l'année en préfixe et le suffixe d'export
         * Google Drive, puis remet une majuscule.
         */
        suggestProjectName(filename) {
            const base = filename
                .replace(/\.zip$/i, '')
                .replace(/-\d{8}T\d{6}Z-\d+-\d{3}$/, '')
                .replace(/^\d{4}[_-]/, '')
                .replace(/[_-]+/g, ' ')
                .trim();

            return base.charAt(0).toUpperCase() + base.slice(1);
        },
        handleRestorePlans() {
            this.isRestorePending = true;

            this.$store.dispatch('source_imports/restorePlans', this.current.id)
                .then(created => {
                    if (created) {
                        this.current = created;
                    }
                })
                .finally(() => {
                    this.isRestorePending = false;
                });
        },
        handleSubmit() {
            const payload = {...this.form};

            if (this.target === 'new') {
                payload.project_id = null;
                payload.building = null;
            } else {
                payload.project_name = null;
                payload.topology = null;
                payload.corporation_id = null;
            }

            this.$store.dispatch('source_imports/store', {single: payload}).then(created => {
                if (created) {
                    this.current = created;
                    this.form.archive = null;
                }
            });
        },
        pollRunningImport() {
            this.now = Date.now();

            const running = this.current && !this.isFinished(this.current)
                ? this.current
                : this.$store.getters['source_imports/running'];

            if (!running) {
                return;
            }

            this.$store.dispatch('source_imports/refresh', running.id).then(updated => {
                if (updated) {
                    this.current = updated;
                }
            });
        },
    },
    components: {
        PageHeader,
        Loader,
        FilePckr,
        Input,
        Select,
        Switch,
        StatusBadge,
        DeliveryIssues,
    }
}
</script>
